import { useDispatch, useSelector } from "react-redux";
import { Content } from "../../../enums/Content";
import { RootState } from "../../../../store";
import { useCallback, useEffect, useMemo, useState } from "react";
import { setTipsAuthors } from "../../../../store/filters/tipFilters.slice";
import {
  Autocomplete,
  Box,
  Button,
  ClickAwayListener,
  TextField,
} from "@mui/material";
import classes from "../Filter.module.scss";
import DetectMobileHelper from "../../../helpers/detect-mobile.helper";
import {
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
} from "@mui/icons-material/";
import AuthorsService from "../../../services/authors.service";
import { Author } from "../../../interfaces/Author";
import { useErrorBoundary } from "react-error-boundary";

const AuthorFilter = ({ content }: { content: Content }) => {
  const { showBoundary } = useErrorBoundary();
  const dispatch = useDispatch();
  const currentAuthors = useSelector(
    (state: RootState) => state.tipFilters.tipFilters.authors
  );
  const [isDropdownOpened, setIsDropdownOpened] = useState<boolean>(false);
  const [authorOptions, setAuthorOptions] = useState<Author[]>([]);
  const [selectedAuthors, setSelectedAuthors] = useState<Author[]>(
    currentAuthors || []
  );
  const [searchInput, setSearchInput] = useState<string>('');

  useEffect(() => {
    if (isDropdownOpened && authorOptions.length === 0) {
      AuthorsService.getAll()
        .then((data: Author[]) => {
          setAuthorOptions(data);
        })
        .catch((error: Error) => showBoundary(error));
    }
  }, [authorOptions.length, isDropdownOpened, showBoundary]);

  useEffect(() => {
    setSelectedAuthors(currentAuthors || []);
  }, [currentAuthors]);

  useEffect(() => {
    const abortController = new AbortController();

    if (isDropdownOpened) {
      if (searchInput.length === 0) {
        AuthorsService.getAll()
          .then((data) => {
            setAuthorOptions(data);
          })
          .catch((error: Error) => showBoundary(error));
      } else {
        AuthorsService.getByName(searchInput, '')
          .then((authors: Author[]) => {
            setAuthorOptions(authors);
          })
          .catch((error: Error) => showBoundary(error));
      }
    }

    return () => abortController.abort();
  }, [searchInput, isDropdownOpened, showBoundary]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsDropdownOpened(!isDropdownOpened);

    if (isDropdownOpened) {
      dispatch(setTipsAuthors(selectedAuthors));
    }
  };

  const handleClose = () => {
    dispatch(setTipsAuthors(selectedAuthors));
    setIsDropdownOpened(false);
    setSearchInput('');
  };

  const handleInputChange = useCallback((event: React.SyntheticEvent, value: string) => {
    setSearchInput(value);
  }, []);

  const renderAutocomplete = useMemo(() => {
    return (
      <Autocomplete
        multiple
        className={`${classes["search"]} mt-2`}
        classes={{
          option: classes["option-color"],
          listbox: `${classes["listbox-height"]} scrollbar`,
          noOptions: classes["no-options"],
        }}
        options={authorOptions}
        getOptionLabel={(option) => option.attributes.name}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            placeholder="Search Authors..."
            InputProps={{
              ...params.InputProps,
              classes: {
                notchedOutline: classes["autocomplete-search"],
              },
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText="No authors found"
        open={true}
        limitTags={1}
        onChange={(event, newValue) => {
          setSelectedAuthors(newValue);
          dispatch(setTipsAuthors(newValue));
        }}
        onInputChange={handleInputChange}
        inputValue={searchInput}
        value={selectedAuthors}
        popupIcon={<SearchIcon className={`${classes["rotate-icon"]} pe-1`} />}
      />
    );
  }, [authorOptions, selectedAuthors, dispatch, handleInputChange, searchInput]);

  const getFiltersLabel = useMemo(() => {
    if (selectedAuthors && selectedAuthors.length > 0) {
      if (selectedAuthors.length === 1) {
        return selectedAuthors[0].attributes.name;
      }
      return `${selectedAuthors.length} Authors`;
    }

    return "Authors";
  }, [selectedAuthors]);

  return (
    <Box>
      <Button
        onClick={handleClick}
        className={`button-text filter-button ${isDropdownOpened ? classes.selectedFilterButton : classes.filterButton
          }`}
        endIcon={isDropdownOpened ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      >
        {getFiltersLabel}
      </Button>
      <Box>
        {isDropdownOpened &&
          (DetectMobileHelper.isMobile() ? (
            <>{renderAutocomplete}</>
          ) : (
            <ClickAwayListener onClickAway={handleClose}>
              {renderAutocomplete}
            </ClickAwayListener>
          ))}
      </Box>
    </Box>
  );
};

export default AuthorFilter;
